import { IMedia } from '@app/shared/interfaces/media.interface';
import {
  isVideoFilePath,
  mediaDisplayName,
  resolveVideoPosterPath,
} from '@app/shared/utils/media.util';
import { IProduct } from '../interfaces/product.interface';
function mediaFromIds(ids: string[] | null | undefined): IMedia[] {
  return (ids ?? []).map(id => ({
    _id: id,
    url: id.startsWith('/') ? id : `/wejoiner/files/${id}`,
  }));
}

export function resolveProductVideos(
  product: Pick<IProduct, 'videos' | 'video_ids'>
): IMedia[] {
  if (product.videos?.length) {
    return product.videos;
  }
  return mediaFromIds(product.video_ids);
}

export function resolveProductCatalogs(
  product: Pick<IProduct, 'catalogs' | 'catalog_ids'>
): IMedia[] {
  if (product.catalogs?.length) {
    return product.catalogs;
  }
  return mediaFromIds(product.catalog_ids);
}

export function hasProductVideos(
  product: Pick<IProduct, 'videos' | 'video_ids'>
): boolean {
  return resolveProductVideos(product).length > 0;
}

export function productMediaDisplayName(media: IMedia): string {
  return mediaDisplayName(media);
}

export { isVideoFilePath, resolveVideoPosterPath };

const IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|gif|bmp|svg)$/i;

export function isCatalogPdf(media: IMedia): boolean {
  if (media.type?.toLowerCase() === 'application/pdf') {
    return true;
  }
  const name = (media.name ?? media.url ?? '').toLowerCase();
  return name.split('?')[0].endsWith('.pdf');
}

export function isCatalogImage(media: IMedia): boolean {
  const type = media.type?.toLowerCase() ?? '';
  if (type.startsWith('image/')) {
    return true;
  }
  const path = (media.name ?? media.url ?? '').toLowerCase().split('?')[0];
  return IMAGE_EXTENSIONS.test(path);
}

export function partitionCatalogMedia(catalogs: IMedia[]): {
  pdfs: IMedia[];
  images: IMedia[];
} {
  const pdfs: IMedia[] = [];
  const images: IMedia[] = [];

  for (const item of catalogs) {
    if (isCatalogPdf(item)) {
      pdfs.push(item);
    } else if (isCatalogImage(item)) {
      images.push(item);
    } else {
      pdfs.push(item);
    }
  }

  return { pdfs, images };
}

export function formatCatalogFileSize(bytes?: number): string {
  if (bytes == null || bytes <= 0 || Number.isNaN(bytes)) {
    return '';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  const precision = unitIndex === 0 ? 0 : 1;
  return `${size.toFixed(precision)} ${units[unitIndex]}`;
}
